import React from 'react';
import { Box, Group, Stack, Text, Badge, ScrollArea, Card, Avatar } from '@mantine/core';
import { FileText, Building2, Calendar, AlertCircle } from 'lucide-react';
import { Loan, PipelineSection } from '../types/loan';
interface LoanKanbanBoardProps {
  loans: Record<string, Loan[]>;
  onLoanClick: (loan: Loan) => void;
}
const STAGES: {
  id: PipelineSection;
  label: string;
  color: string;
  bg: string;
}[] = [{
  id: 'CTC',
  label: 'CTC',
  color: '#4ade80',
  bg: 'rgba(74, 222, 128, 0.15)'
}, {
  id: 'CD OUT',
  label: 'CD OUT',
  color: '#60a5fa',
  bg: 'rgba(96, 165, 250, 0.15)'
}, {
  id: 'ALL IN',
  label: 'ALL IN',
  color: '#c084fc',
  bg: 'rgba(192, 132, 252, 0.15)'
}, {
  id: 'AWC',
  label: 'AWC',
  color: '#fbbf24',
  bg: 'rgba(251, 191, 36, 0.15)'
}, {
  id: 'SUBMITTED',
  label: 'SUBMITTED',
  color: '#94a3b8',
  bg: 'rgba(148, 163, 184, 0.15)'
}];
export function LoanKanbanBoard({
  loans,
  onLoanClick
}: LoanKanbanBoardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const getDaysColor = (days: number) => {
    if (days >= 5) return 'red';
    if (days >= 3) return 'orange';
    return 'green';
  };
  return <ScrollArea type="auto" offsetScrollbars data-id="element-231">
      <Group align="flex-start" wrap="nowrap" gap="md" pb="md" style={{
      minWidth: 'max-content'
    }} data-id="element-232">
        {STAGES.map(stage => {
        const stageLoans = loans[stage.id] || [];
        const totalVolume = stageLoans.reduce((sum, loan) => sum + loan.amount, 0);
        return <Box key={stage.id} style={{
          width: 320,
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '800px'
        }} data-id="element-233">
              {/* Column Header */}
              <Box style={{
            padding: '16px',
            borderBottom: `2px solid ${stage.color}`,
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }} data-id="element-234">
                <Group justify="space-between" mb="xs" data-id="element-235">
                  <Group gap="xs" data-id="element-236">
                    <Box style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: stage.color,
                  boxShadow: `0 0 8px ${stage.color}`
                }} data-id="element-237" />
                    <Text fw={700} style={{
                  color: 'rgba(255, 255, 255, 0.9)'
                }} data-id="element-238">
                      {stage.label}
                    </Text>
                  </Group>
                  <Badge variant="filled" style={{
                backgroundColor: stage.bg,
                color: stage.color,
                fontWeight: 700
              }} data-id="element-239">
                    {stageLoans.length}
                  </Badge>
                </Group>
                <Text size="sm" fw={600} style={{
              color: 'rgba(255, 255, 255, 0.6)'
            }} data-id="element-240">
                  {formatCurrency(totalVolume)}
                </Text>
              </Box>

              {/* Column Content */}
              <ScrollArea style={{
            flex: 1
          }} p="sm" data-id="element-241">
                <Stack gap="sm" data-id="element-242">
                  {stageLoans.map(loan => {
                const incompleteConditions = [...loan.outstandingConditions, ...loan.loanOfficerConditions].filter(c => !c.completed);
                return <Card key={loan.id} onClick={() => onLoanClick(loan)} style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                  }
                }} padding="md" radius="md" data-id="element-243">
                        <Group justify="space-between" align="flex-start" mb="sm" data-id="element-244">
                          <Group gap="xs" data-id="element-245">
                            <FileText size={16} color="rgba(255, 255, 255, 0.5)" data-id="element-246" />
                            <Text fw={700} size="sm" style={{
                        color: 'rgba(255, 255, 255, 0.9)'
                      }} data-id="element-247">
                              {loan.loanNumber}
                            </Text>
                          </Group>
                          <Badge variant="light" color={getDaysColor(loan.daysInStage)} size="sm" radius="sm" data-id="element-248">
                            {loan.daysInStage}d
                          </Badge>
                        </Group>

                        <Group gap="sm" mb="md" data-id="element-249">
                          <Avatar size="sm" radius="xl" color="blue" name={loan.clientName} data-id="element-250">
                            {loan.clientName.charAt(0)}
                          </Avatar>
                          <Box data-id="element-251">
                            <Text fw={600} size="sm" style={{
                        color: 'rgba(255, 255, 255, 0.9)'
                      }} lh={1.2} data-id="element-252">
                              {loan.clientName}
                            </Text>
                            <Text size="xs" style={{
                        color: 'rgba(255, 255, 255, 0.5)'
                      }} lh={1.2} data-id="element-253">
                              {loan.program} • {loan.state}
                            </Text>
                          </Box>
                        </Group>

                        <Group justify="space-between" align="flex-end" data-id="element-254">
                          <Stack gap={4} data-id="element-255">
                            <Group gap={6} data-id="element-256">
                              <Building2 size={14} color="rgba(255, 255, 255, 0.4)" data-id="element-257" />
                              <Text size="xs" style={{
                          color: 'rgba(255, 255, 255, 0.6)'
                        }} data-id="element-258">
                                {loan.lender}
                              </Text>
                            </Group>
                            {incompleteConditions.length > 0 && <Group gap={4} data-id="element-259">
                                <AlertCircle size={12} color="#fb923c" data-id="element-260" />
                                <Text size="xs" style={{
                          color: '#fb923c'
                        }} data-id="element-261">
                                  {incompleteConditions.length} pending
                                </Text>
                              </Group>}
                          </Stack>
                          <Text fw={700} size="md" style={{
                      color: 'rgba(255, 255, 255, 0.9)'
                    }} data-id="element-262">
                            {formatCurrency(loan.amount)}
                          </Text>
                        </Group>
                      </Card>;
              })}
                  {stageLoans.length === 0 && <Box py="xl" ta="center" data-id="element-263">
                      <Text size="sm" style={{
                  color: 'rgba(255, 255, 255, 0.3)'
                }} data-id="element-264">
                        No loans in this stage
                      </Text>
                    </Box>}
                </Stack>
              </ScrollArea>
            </Box>;
      })}
      </Group>
    </ScrollArea>;
}